import Dexie from 'dexie';
import { AppSettings, Category, RecurringExpense, SavingsGoal, Tag, Transaction } from './types';

const SAMPLE_CATEGORY_IDS = ['cat-courses', 'cat-loisirs', 'cat-transport'];
const SAMPLE_TAG_IDS = ['tag-lidl', 'tag-uber', 'tag-cinema'];
const SAMPLE_TRANSACTION_IDS = ['tx-1', 'tx-2', 'tx-3'];

export class BudgetDB extends Dexie {
  categories: Dexie.Table<Category, string>;
  tags: Dexie.Table<Tag, string>;
  transactions: Dexie.Table<Transaction, string>;
  recurringExpenses: Dexie.Table<RecurringExpense, string>;
  savingsGoals: Dexie.Table<SavingsGoal, string>;
  settings: Dexie.Table<AppSettings, string>;

  constructor() {
    super('budgetSimpleDB');
    this.version(1).stores({
      categories: '&id, name, monthlyBudget, updatedAt',
      tags: '&id, name, categoryId, updatedAt',
      transactions: '&id, date, type, categoryId, updatedAt',
    });

    this.version(2)
      .stores({
        categories: '&id, name, monthlyBudget, updatedAt',
        tags: '&id, name, categoryId, updatedAt',
        transactions: '&id, date, type, categoryId, updatedAt',
      })
      .upgrade(async (transaction) => {
        await transaction.table('transactions').bulkDelete(SAMPLE_TRANSACTION_IDS);
        await transaction.table('tags').bulkDelete(SAMPLE_TAG_IDS);

        await Promise.all(
          SAMPLE_CATEGORY_IDS.map(async (categoryId) => {
            const usageCount = await transaction.table('transactions').where('categoryId').equals(categoryId).count();
            if (usageCount === 0) {
              await transaction.table('categories').delete(categoryId);
            }
          })
        );
      });

    this.version(3).stores({
      categories: '&id, name, monthlyBudget, updatedAt',
      tags: '&id, name, categoryId, updatedAt',
      transactions: '&id, date, type, categoryId, updatedAt',
      recurringExpenses: '&id, name, active, updatedAt',
      savingsGoals: '&id, month, updatedAt',
    });

    this.version(4).stores({
      categories: '&id, name, monthlyBudget, updatedAt',
      tags: '&id, name, categoryId, updatedAt',
      transactions: '&id, date, type, categoryId, updatedAt',
      recurringExpenses: '&id, name, active, updatedAt',
      savingsGoals: '&id, month, updatedAt',
      settings: '&id, updatedAt',
    });

    this.categories = this.table('categories');
    this.tags = this.table('tags');
    this.transactions = this.table('transactions');
    this.recurringExpenses = this.table('recurringExpenses');
    this.savingsGoals = this.table('savingsGoals');
    this.settings = this.table('settings');
  }
}

let dbInstance: BudgetDB | null = null;

export function getDb() {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!('indexedDB' in window)) {
    return null;
  }

  if (!dbInstance) {
    try {
      dbInstance = new BudgetDB();
    } catch (err) {
      return null;
    }
  }

  return dbInstance;
}
